import { Form, Formik, useField, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import {
  getIaEstimate,
  importBankAccount,
  listImportableBankAccounts,
} from "@/features/forms/api";
import {
  FILE_AS_OPTIONS,
  type FileAs,
  type Form9465Data,
  type FormSubmission,
  type IaEstimate,
  type ImportableBankAccount,
  PAY_PERIOD_OPTIONS,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikRadioYN, FormikSelect } from "./formik-controls";

const PAYMENT_DAYS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const CALL_TIMES = [
  "8:00 a.m. - 10:00 a.m.",
  "10:00 a.m. - 12:00 p.m.",
  "12:00 p.m. - 2:00 p.m.",
  "2:00 p.m. - 4:00 p.m.",
  "4:00 p.m. - 6:00 p.m.",
].map((t) => ({ value: t, label: t }));

/** IRS 9465 flags a below-minimum plan / a balance above $50k for a 433-F. */
const IA_433F_THRESHOLD = 50_000;

const money = Yup.string().test(
  "non-negative",
  "Must be zero or greater",
  (v) => v == null || v === "" || (!Number.isNaN(Number(v)) && Number(v) >= 0),
);

const count = Yup.string().test(
  "1-2 digits",
  "Enter a 1–2 digit number",
  (v) => v == null || v === "" || (/^\d{1,2}$/.test(v) && Number(v) <= 99),
);

const schema = Yup.object({
  for_who: Yup.string().oneOf(["T", "S", "J"]).required(),
  tax_form_no: Yup.string().max(70, "Too long"),
  total_amount: money,
  add_total_amount: money,
  tax_amt_pay: money,
  tax_pay_per_month: money,
  net_income: money,
  sp_net_income: money,
  health_premium: money,
  court_amount: money,
  child_care: money,
  dependents: count,
  over_65: count,
  vehicles: count,
  car_payments: count,
  ein: Yup.string().test(
    "ein",
    "EIN must be 9 digits",
    (v) => !v || v.replace(/\D/g, "").length === 9,
  ),
  routing_number: Yup.string()
    .matches(/^\d*$/, "Digits only")
    .test("len", "Must be 9 digits", (v) => !v || v.replace(/\D/g, "").length === 9),
  account_number: Yup.string().matches(/^\d*$/, "Digits only").max(17, "At most 17 digits"),
  tax_pay_date: Yup.string(),
});

function toInitialValues(data: Record<string, unknown>): Form9465Data {
  const d = data as Partial<Form9465Data>;
  return {
    for_who: d.for_who ?? "T",
    tax_form_no: d.tax_form_no ?? "1040",
    tax_periods: d.tax_periods ?? "",
    total_amount: d.total_amount ?? "0",
    add_total_amount: d.add_total_amount ?? "0",
    tax_amt_pay: d.tax_amt_pay ?? "0",
    tax_pay_per_month: d.tax_pay_per_month ?? "0",
    tax_pay_date: d.tax_pay_date ?? "",
    best_time_home: d.best_time_home ?? "",
    best_time_work: d.best_time_work ?? "",
    new_address: d.new_address ?? false,
    for_business: d.for_business ?? false,
    business_name: d.business_name ?? "",
    ein: d.ein ?? "",
    routing_number: d.routing_number ?? "",
    account_number: d.account_number ?? "",
    no_electronic: d.no_electronic ?? false,
    payroll_deduction: d.payroll_deduction ?? false,
    county: d.county ?? "",
    share_expenses: d.share_expenses ?? false,
    dependents: d.dependents ?? "",
    over_65: d.over_65 ?? "",
    pay_period: d.pay_period ?? "",
    net_income: d.net_income ?? "",
    sp_pay_period: d.sp_pay_period ?? "",
    sp_net_income: d.sp_net_income ?? "",
    vehicles: d.vehicles ?? "",
    car_payments: d.car_payments ?? "",
    health_insurance: d.health_insurance ?? false,
    health_deducted: d.health_deducted ?? "",
    health_premium: d.health_premium ?? "",
    court_payments: d.court_payments ?? false,
    court_deducted: d.court_deducted ?? "",
    court_amount: d.court_amount ?? "",
    child_care: d.child_care ?? "",
  };
}

function num(v: string | undefined): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function currency(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function LabeledCell({
  name,
  label,
  ...props
}: { name: string; label: string } & React.ComponentProps<typeof FormikCell>) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={name} className="text-sm font-normal">
        {label}
      </Label>
      <FormikCell name={name} {...props} />
    </div>
  );
}

function LabeledSelect({
  name,
  label,
  options,
  placeholder,
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-sm font-normal">{label}</Label>
      <FormikSelect name={name} options={options} placeholder={placeholder} />
    </div>
  );
}

function FileAsField({ married }: { married: boolean }) {
  const [field, , helpers] = useField<FileAs>("for_who");
  if (!married) return <p className="text-muted-foreground text-sm">Taxpayer</p>;
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as FileAs)}
    >
      {FILE_AS_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`fw-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`fw-${o.value}`} value={o.value} />
          {o.value === "T" ? "Taxpayer Only" : o.value === "S" ? "Spouse Only" : "Jointly"}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** Live payment-calculation lines (total due / amount owed / ÷72 minimum) plus
 * the read-only estimated IA payment and the 433-F warning banners. */
function PaymentCalculations({ estimate }: { estimate: IaEstimate | null }) {
  const { values } = useFormikContext<Form9465Data>();
  const totalDue = num(values.total_amount) + num(values.add_total_amount);
  const amountOwed = totalDue - num(values.tax_amt_pay);
  const netBy72 = amountOwed > 0 ? Math.ceil(amountOwed / 72) : 0;
  const monthly = num(values.tax_pay_per_month);
  const belowMinimum = monthly > 0 && monthly < netBy72;
  const overThreshold = amountOwed > IA_433F_THRESHOLD;

  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Payment calculations</SectionHeading>
      <div className="grid gap-4 sm:grid-cols-2">
        <LabeledCell name="total_amount" label="Total tax due (line 5, $)" inputMode="decimal" />
        <LabeledCell
          name="add_total_amount"
          label="Additional balances due (line 6, $)"
          inputMode="decimal"
        />
      </div>
      <dl className="bg-muted/40 grid gap-3 rounded-md p-4 sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground text-xs">Total amount due (line 7)</dt>
          <dd className="text-foreground font-semibold">${currency(totalDue)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">Amount owed (line 9)</dt>
          <dd className="text-foreground font-semibold">${currency(amountOwed)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">Minimum ÷ 72 (line 10)</dt>
          <dd className="text-foreground font-semibold">${currency(netBy72)}</dd>
        </div>
      </dl>
      <div className="grid gap-4 sm:grid-cols-2">
        <LabeledCell name="tax_amt_pay" label="Payment with this request (line 8, $)" inputMode="decimal" />
        <div className="grid gap-1.5">
          <Label className="text-sm font-normal">Estimated IA monthly payment (Tier 2.2)</Label>
          <div
            aria-label="Estimated IA monthly payment"
            className="border-input bg-muted/40 flex h-9 items-center rounded-md border px-3 text-sm"
          >
            {estimate
              ? `$${currency(num(estimate.monthlyPayment))} · ${estimate.iaType}`
              : "No worksheet yet"}
          </div>
        </div>
        <LabeledCell
          name="tax_pay_per_month"
          label="Amount you can pay each month (line 11a, $)"
          inputMode="decimal"
        />
        <LabeledSelect name="tax_pay_date" label="Payment day of month (line 11b)" options={PAYMENT_DAYS} placeholder="Day" />
      </div>
      {estimate && (estimate.missingCseds.length > 0 || estimate.expiredCseds.length > 0) && (
        <p className="text-warning text-sm">
          {estimate.missingCseds.length > 0 &&
            `Missing CSED for ${estimate.missingCseds.join(", ")} (assumed 10 years). `}
          {estimate.expiredCseds.length > 0 &&
            `Expired CSED for ${estimate.expiredCseds.join(", ")}.`}
        </p>
      )}
      {belowMinimum && (
        <p className="text-destructive text-sm" role="alert">
          Your monthly payment is below the ÷72 minimum — attach Form 433-F.
        </p>
      )}
      {overThreshold && (
        <p className="text-destructive text-sm" role="alert">
          Amount owed is over $50,000 — attach Form 433-F.
        </p>
      )}
      <div className="flex flex-wrap gap-6">
        <FormikCheckbox
          name="no_electronic"
          label="Low-income status (unable to make electronic payments)"
        />
        <FormikCheckbox
          name="payroll_deduction"
          label="Pay by payroll deduction (attach Form 2159)"
        />
      </div>
    </Card>
  );
}

/** Pick a stored personal (433-A) account and import its number. */
function BankImportField({ submissionId }: { submissionId: string }) {
  const { setFieldValue } = useFormikContext<Form9465Data>();
  const [accounts, setAccounts] = useState<ImportableBankAccount[]>([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listImportableBankAccounts(submissionId)
      .then((a) => active && setAccounts(a))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [submissionId]);

  if (accounts.length === 0) return null;

  async function handleImport() {
    if (!selected) return;
    setBusy(true);
    try {
      const { accountNumber } = await importBankAccount(submissionId, selected);
      await setFieldValue("account_number", accountNumber);
      await setFieldValue("routing_number", "");
      toast.success("Imported account number from 433-A");
    } catch {
      toast.error("Couldn't import that account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="grid gap-1.5">
        <Label className="text-sm font-normal">Import from 433-A</Label>
        <select
          aria-label="Personal account to import"
          className="border-input bg-background h-9 rounded-md border px-3 text-sm"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">Select an account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.institution} {a.masked}
            </option>
          ))}
        </select>
      </div>
      <Button type="button" variant="outline" size="sm" disabled={!selected || busy} onClick={handleImport}>
        {busy ? "Importing…" : "Import"}
      </Button>
    </div>
  );
}

/** The line-2 defunct-business block, revealed by the for_business checkbox. */
function BusinessBlock() {
  const [field] = useField<boolean>({ name: "for_business", type: "checkbox" });
  if (!field.checked) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <LabeledCell name="business_name" label="Business name" maxLength={55} />
      <LabeledCell name="ein" label="EIN" inputMode="numeric" maxLength={10} />
    </div>
  );
}

/** Part II Additional Information (the $25k–$50k / below-minimum block). */
function AdditionalInformation() {
  const [health] = useField<boolean>({ name: "health_insurance", type: "checkbox" });
  const [court] = useField<boolean>({ name: "court_payments", type: "checkbox" });
  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Additional information ($25k–$50k / below-minimum)</SectionHeading>
      <div className="grid gap-4 sm:grid-cols-3">
        <LabeledCell name="county" label="County of primary residence" />
        <LabeledCell name="dependents" label="Dependents claimed" inputMode="numeric" maxLength={2} />
        <LabeledCell name="over_65" label="People in household 65+" inputMode="numeric" maxLength={2} />
        <LabeledCell name="vehicles" label="Vehicles owned" inputMode="numeric" maxLength={2} />
        <LabeledCell name="car_payments" label="Car payments / month" inputMode="numeric" maxLength={2} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <LabeledSelect name="pay_period" label="How often are you paid?" options={PAY_PERIOD_OPTIONS} placeholder="Pay period" />
        <LabeledCell name="net_income" label="Net income per pay period ($)" inputMode="decimal" />
        <LabeledSelect name="sp_pay_period" label="Spouse pay period" options={PAY_PERIOD_OPTIONS} placeholder="Pay period" />
        <LabeledCell name="sp_net_income" label="Spouse net income per period ($)" inputMode="decimal" />
      </div>
      <FormikCheckbox name="share_expenses" label="I share household expenses" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <FormikCheckbox name="health_insurance" label="I have health insurance" />
          {health.checked && (
            <div className="grid gap-2 pl-6">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-normal">Premiums deducted from pay?</Label>
                <FormikRadioYN name="health_deducted" aria-label="Health premiums deducted" />
              </div>
              <LabeledCell name="health_premium" label="Monthly premium (if not deducted, $)" inputMode="decimal" />
            </div>
          )}
        </div>
        <div className="grid gap-2">
          <FormikCheckbox name="court_payments" label="I make court-ordered payments" />
          {court.checked && (
            <div className="grid gap-2 pl-6">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-normal">Deducted from pay?</Label>
                <FormikRadioYN name="court_deducted" aria-label="Court payments deducted" />
              </div>
              <LabeledCell name="court_amount" label="Court-ordered amount / month (if not deducted, $)" inputMode="decimal" />
            </div>
          )}
        </div>
      </div>
      <LabeledCell name="child_care" label="Child/dependent care paid per month ($)" inputMode="decimal" />
    </Card>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form 9465 (Installment Agreement Request) and its Spanish variant.
 * Payment terms are entered here; identity + tax periods prefill from the client
 * + transcripts, and the estimated IA payment is read (never re-derived) from
 * the Tier 2.2 worksheet calc. The Spanish variant hides the 433-A bank import.
 */
export function Form9465Editor({ client, submission, onSubmissionChange }: Props) {
  const spanish = submission.formNumber.includes("sp");
  const married = client.maritalStatus === "MARRIED";
  const [estimate, setEstimate] = useState<IaEstimate | null>(null);
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  useEffect(() => {
    let active = true;
    getIaEstimate(submission.id)
      .then((e) => active && setEstimate(e))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [submission.id]);

  return (
    <Formik initialValues={toInitialValues(submission.data)} validationSchema={schema} onSubmit={() => {}}>
      <Form className="flex flex-col gap-6">
        <Card className="gap-4 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-foreground font-semibold">Generate for</span>
            <FileAsField married={married} />
          </div>
          <div className="text-muted-foreground text-sm">
            <p className="text-foreground font-medium">{client.name}</p>
            {address && <p>{address}</p>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledSelect name="best_time_home" label="Best time to call (home)" options={CALL_TIMES} placeholder="Any time" />
            <LabeledSelect name="best_time_work" label="Best time to call (work)" options={CALL_TIMES} placeholder="Any time" />
          </div>
          <div className="flex flex-wrap gap-6">
            <FormikCheckbox name="new_address" label="This address differs from IRS records" />
            <FormikCheckbox name="for_business" label="For a business no longer operating" />
          </div>
          <BusinessBlock />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Tax form(s) &amp; periods</SectionHeading>
          <LabeledCell name="tax_form_no" label="Tax form number(s)" placeholder="1040" maxLength={70} />
          <div className="grid gap-1.5">
            <Label htmlFor="tax_periods" className="text-sm font-normal">
              Tax periods
            </Label>
            <TaxPeriodsField />
          </div>
        </Card>

        <PaymentCalculations estimate={estimate} />

        <Card className="gap-4 p-6">
          <SectionHeading>Bank account (Direct Debit)</SectionHeading>
          {!spanish && <BankImportField submissionId={submission.id} />}
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="routing_number" label="Routing number (9 digits)" inputMode="numeric" maxLength={9} />
            <LabeledCell name="account_number" label="Account number (≤ 17 digits)" inputMode="numeric" maxLength={17} />
          </div>
        </Card>

        <AdditionalInformation />

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}

function TaxPeriodsField() {
  const [field] = useField("tax_periods");
  return <Textarea id="tax_periods" rows={2} {...field} />;
}
